/**
 * CodeInput - 인증 코드 입력 컴포넌트 (OTP 등)
 *
 * 지정된 자릿수만큼 개별 숫자 입력 필드를 렌더링.
 * 자동 포커스 이동, 붙여넣기, 백스페이스 처리를 지원한다.
 * @param length - 자릿수 (기본값 6)
 * @param onComplete - 모든 자리 입력 완료 시 호출
 */
'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import styles from './CodeInput.module.css';

export interface CodeInputProps {
  /** Number of digits (default 6) */
  length?: number;
  /** Current value */
  value?: string;
  /** Called when the full value changes */
  onChange?: (value: string) => void;
  /** Called when all digits are filled */
  onComplete?: (value: string) => void;
  /** Show error styling */
  error?: boolean;
  /** Disable all inputs */
  disabled?: boolean;
  /** Auto-focus the first input on mount */
  autoFocus?: boolean;
}

export default function CodeInput({
  length = 6,
  value = '',
  onChange,
  onComplete,
  error = false,
  disabled = false,
  autoFocus = true,
}: CodeInputProps) {
  const [digits, setDigits] = useState<string[]>(() => {
    const initial = Array(length).fill('');
    for (let i = 0; i < Math.min(value.length, length); i++) {
      initial[i] = value[i] || '';
    }
    return initial;
  });

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync external value changes
  useEffect(() => {
    const newDigits = Array(length).fill('');
    for (let i = 0; i < Math.min(value.length, length); i++) {
      newDigits[i] = value[i] || '';
    }
    setDigits(newDigits);
  }, [value, length]);

  // Auto-focus on mount
  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const focusInput = useCallback(
    (index: number) => {
      if (index >= 0 && index < length) {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
      }
    },
    [length],
  );

  const emitChange = useCallback(
    (newDigits: string[]) => {
      const joined = newDigits.join('');
      onChange?.(joined);
      if (joined.length === length && newDigits.every((d) => d !== '')) {
        onComplete?.(joined);
      }
    },
    [length, onChange, onComplete],
  );

  const handleChange = useCallback(
    (index: number, val: string) => {
      const digit = val.replace(/\D/g, '').slice(-1);
      setDigits((prev) => {
        const next = [...prev];
        next[index] = digit;
        emitChange(next);
        return next;
      });
      if (digit && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [length, focusInput, emitChange],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!digits[index] && index > 0) {
          e.preventDefault();
          setDigits((prev) => {
            const next = [...prev];
            next[index - 1] = '';
            emitChange(next);
            return next;
          });
          focusInput(index - 1);
        } else {
          setDigits((prev) => {
            const next = [...prev];
            next[index] = '';
            emitChange(next);
            return next;
          });
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }
    },
    [digits, length, focusInput, emitChange],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (!pasted.length) return;

      const newDigits = Array(length).fill('');
      for (let i = 0; i < pasted.length; i++) {
        newDigits[i] = pasted[i];
      }
      setDigits(newDigits);
      emitChange(newDigits);
      focusInput(Math.min(pasted.length, length - 1));
    },
    [length, focusInput, emitChange],
  );

  return (
    <div className={styles.digitRow}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          placeholder="-"
          className={`${styles.digitInput}${error ? ` ${styles.hasError}` : ''}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          autoComplete="one-time-code"
          aria-label={`인증 코드 ${index + 1}번째 자리`}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
